import type { IdentityHealth } from "./schemas";

/**
 * How the two judgement calls on this dashboard are made, in one file with no
 * React in it, because both of them are arguments about the product rather
 * than about rendering -- and both are wrong in a way that costs something.
 */

/**
 * The BACKSTOP a session is stamped with, not a measurement.
 *
 * This said three hours, because the whole system did -- a placeholder from
 * the Python POC that everything downstream treated as a fact about
 * Facebook. It is not: sessions minted 47.5 hours earlier were measured
 * still returning listings, and the cookies behind them expire in 2027. The
 * engine now retires a session when Facebook REJECTS it and uses this only
 * as a far edge (see `DEFAULT_TTL_MS` in @snipe/facebook).
 *
 * Which changes what this page is for. A countdown to a backstop is
 * informational; the numbers that mean something are "no live session",
 * "mints are failing" and "this account keeps failing".
 */
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Where a countdown stops being background information.
 *
 * Six hours is well inside the engine's twelve-hour top-up margin: if a
 * session is this close to its backstop, minting has had half a day to
 * replace it and has not. A day out is worth noticing, not acting on.
 */
export const EXPIRY_CRITICAL_MS = 6 * 60 * 60 * 1000;
export const EXPIRY_WARNING_MS = 24 * 60 * 60 * 1000;

export type SessionUrgency = "gone" | "critical" | "warning" | "healthy" | "idle";

export interface SessionExpiry {
  urgency: SessionUrgency;
  /** Null when there is nothing live to run out. */
  msRemaining: number | null;
  /** How far through a fresh session's backstop this identity is, 0..1. */
  spent: number;
}

/**
 * Time until this identity's first session hits its backstop, and how loudly
 * to say it.
 *
 * `gone` -- no live session at all -- is the state that matters, and it is
 * the one the fleet sat in for an hour while every alert recorded
 * `no_session`. The countdown below it is context: a session is normally
 * retired by Facebook rejecting it, not by reaching this number, so a row
 * with days on the clock can still be one rejection from empty.
 *
 * `paused` and `blocked` identities are reported as `idle` rather than
 * `gone`: an account we took out of rotation on purpose having no sessions is
 * the expected outcome, and colouring it as an emergency trains people to
 * ignore the colour.
 */
export function sessionExpiry(identity: IdentityHealth, now: Date): SessionExpiry {
  if (identity.liveSessions === 0 || identity.soonestExpiryAt === null) {
    return {
      urgency: identity.status === "active" ? "gone" : "idle",
      msRemaining: null,
      spent: 1,
    };
  }

  const msRemaining = Math.max(0, identity.soonestExpiryAt.getTime() - now.getTime());
  const urgency: SessionUrgency =
    msRemaining <= EXPIRY_CRITICAL_MS
      ? "critical"
      : msRemaining <= EXPIRY_WARNING_MS
        ? "warning"
        : "healthy";
  return {
    urgency,
    msRemaining,
    spent: Math.min(1, Math.max(0, 1 - msRemaining / SESSION_TTL_MS)),
  };
}

/** Sorts the identity that needs attention first, without hiding the rest. */
const URGENCY_ORDER: Record<SessionUrgency, number> = {
  gone: 0,
  critical: 1,
  warning: 2,
  healthy: 3,
  idle: 4,
};

export function byUrgency(a: SessionExpiry, b: SessionExpiry): number {
  const order = URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency];
  if (order !== 0) return order;
  return (a.msRemaining ?? Infinity) - (b.msRemaining ?? Infinity);
}

export type OverdueLevel = "normal" | "watch" | "elevated";

/**
 * A non-zero overdue count is the healthy state, not the broken one: an alert
 * is overdue from the instant it becomes due until a worker claims it, so on a
 * busy system the number is never zero and is always moving. A panel that goes
 * red at one overdue alert is a panel that is red all day, and a panel that is
 * red all day is a panel nobody looks at.
 *
 * So the threshold scales with the fleet -- a tenth of the active alerts --
 * with a floor for the small installation where a tenth is nothing.
 */
export const OVERDUE_FLOOR = 3;

export function overdueThreshold(alertsActive: number): number {
  return Math.max(OVERDUE_FLOOR, Math.ceil(alertsActive * 0.1));
}

/**
 * How many consecutive readings above the threshold make it a real signal.
 *
 * The thing worth reacting to is the backlog GROWING AND STAYING GROWN, which
 * a single reading cannot distinguish from a worker that was mid-claim when
 * the page refreshed. Three readings of a fifteen-second poll is about a
 * minute of sustained backlog: long enough that no ordinary claim explains it.
 */
export const OVERDUE_WINDOW = 3;

/**
 * `readings` is oldest-first and may be shorter than the window; a page that
 * has just been opened has not yet earned the right to cry wolf.
 */
export function overdueLevel(readings: readonly number[], threshold: number): OverdueLevel {
  const latest = readings[readings.length - 1];
  if (latest === undefined || latest < threshold) return "normal";
  const window = readings.slice(-OVERDUE_WINDOW);
  const sustained = window.length >= OVERDUE_WINDOW && window.every((n) => n >= threshold);
  return sustained ? "elevated" : "watch";
}

/**
 * The one-line explanation, for the tile's `title` and nothing else.
 *
 * It is a tooltip rather than body text because the tile already states the
 * level in a word: the reason an ordinary overdue count is ordinary is worth
 * having available and is not worth a sentence on screen every time somebody
 * opens the dashboard.
 */
export function describeOverdue(level: OverdueLevel): string {
  switch (level) {
    case "normal":
      return "Alerts are overdue between falling due and being claimed.";
    case "watch":
      return "One reading above the usual. Not a signal yet.";
    case "elevated":
      return "High across several readings. Workers are falling behind.";
  }
}
